import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CellularBillingService } from 'src/app/shared/services/cellular-billing.service';
import { BillingWorkflow, CellularBillingReport, CellularBillingReview, CellularBillingRow } from 'src/app/shared/model/cellular-billing';
import { CellularReviewDecisionDialogComponent } from './cellular-review-decision-dialog.component';

@Component({
  selector: 'app-cellular-report-review',
  templateUrl: './cellular-report-review.component.html',
  styleUrls: ['./cellular-report-review.component.scss']
})
export class CellularReportReviewComponent implements OnInit, OnDestroy {
  report: CellularBillingReport;
  review: CellularBillingReview;
  workflow: BillingWorkflow;
  dirty = false;
  pricingRevision = 0;
  poolingRevision = 0;
  loading = true;
  busy = false;
  exportBusy = false;
  reviewExpanded = false;
  duplicatesOnly = false;
  error = '';
  totalCharge = 0;
  totalUsage = 0;
  readonly comment = new FormControl('', Validators.maxLength(2000));
  readonly months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  private readonly subscriptions = new Subscription();
  private reportId: string;
  private loadRequest: Subscription;

  constructor(private route: ActivatedRoute, private billing: CellularBillingService,
              private dialog: MatDialog, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.paramMap.subscribe(params => {
      this.reportId = params.get('reportId');
      this.load();
    }));
  }

  ngOnDestroy(): void { this.subscriptions.unsubscribe(); }

  load(): void {
    if (this.loadRequest) this.loadRequest.unsubscribe();
    this.loading = true;
    this.error = '';
    this.loadRequest = forkJoin([
      this.billing.getWorkflow(this.reportId), this.billing.getReview(this.reportId)
    ]).pipe(finalize(() => this.loading = false)).subscribe(([workflow, review]) => {
      this.acceptWorkflow(workflow);
      this.review = review;
      this.comment.reset('');
    }, error => this.error = this.message(error, 'Unable to load this report. Please try again.'));
    this.subscriptions.add(this.loadRequest);
  }

  sendForReview(): void {
    if (this.busy || !this.workflow || this.workflow.stage !== 'READY_FOR_REVIEW' || ['COMPLETED', 'CONFLICT'].includes(this.workflow.monthStatus)) return;
    this.busy = true;
    this.subscriptions.add(this.billing.requestReview(this.reportId, window.location.origin)
      .pipe(finalize(() => this.busy = false)).subscribe(() => {
        this.toastr.success('Report sent for verification.');
        this.load();
      }, error => this.toastr.error(this.message(error, 'Unable to send the review email.'))));
  }

  get statusLabel(): string {
    const labels = { READY_FOR_REVIEW: 'Ready for review', REVIEW: 'Pat review', FINAL_BILLING: 'Final billing',
      COMPLETED: 'Billing completed', REVIEW_REQUIRED: 'Revision required', SUPERSEDED: 'Superseded · read only', MONTH_CONFLICT: 'Completion conflict' };
    return this.workflow ? labels[this.workflow.stage] : '';
  }

  get canDecide(): boolean { return this.workflow && this.workflow.stage === 'REVIEW'; }
  get canEdit(): boolean { return !this.busy && !!this.workflow && !['COMPLETED', 'CONFLICT'].includes(this.workflow.monthStatus) && ['REVIEW', 'FINAL_BILLING'].includes(this.workflow.stage); }
  get canRetryEmail(): boolean {
    if (!this.workflow || ['SUPERSEDED', 'MONTH_CONFLICT'].includes(this.workflow.stage)) return false;
    return this.workflow.monthStatus !== 'COMPLETED' || (this.workflow.stage === 'COMPLETED' && this.workflow.reportId === this.workflow.finalReportId && this.workflow.notificationRevision === this.workflow.finalRevision);
  }

  markEdited(): void {
    this.dirty = true;
    this.poolingRevision = (this.poolingRevision || 0) + 1;
    this.totalCharge = this.cellularRows.filter(row => row.verificationGb >= (this.report.thresholdGb || 0)).reduce((sum, row) => sum + Number(row.gigsToCharge || 0), 0);
    this.totalUsage = this.cellularRows.reduce((sum, row) => sum + Number(row.verificationGb || 0), 0);
  }
  configurationChanged(): void { this.pricingRevision++; this.markEdited(); }
  refreshBundleMapping(): void {
    if (!this.canEdit || !this.report.overlayRows) return;
    this.subscriptions.add(this.billing.mapOverlay(this.report.overlayRows, this.report.month, this.report.year).subscribe(rows => {
      this.report.overlayRows = rows; this.configurationChanged();
    }, error => this.toastr.error(this.message(error, 'Unable to refresh bundle mappings.'))));
  }
  private acceptWorkflow(workflow: BillingWorkflow): void {
    this.workflow = workflow; this.report = workflow.report;
    this.markEdited(); this.dirty = false;
  }

  get cellularRows(): CellularBillingRow[] { return this.report.pooling && [2, 3].includes(this.report.pooling.version) ? this.report.pooling.roster : this.report.rows || []; }
  get reconciliationDecisions() {
    return this.report && this.report.reconciliation ? Object.keys(this.report.reconciliation.decisions).map(mac => ({ mac, ...this.report.reconciliation.decisions[mac] })) : [];
  }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty || this.busy) { event.preventDefault(); event.returnValue = ''; }
  }
  confirmDiscardChanges(): boolean { return !this.busy && (!this.dirty || window.confirm('Discard unsaved billing edits?')); }
  reloadWorkflow(): void { if (this.confirmDiscardChanges()) this.load(); }

  saveWorkflow(action: 'SAVE' | 'SUBMIT_BILLING' | 'COMPLETE' | 'SEND_BACK'): void {
    if (!this.canEdit) return;
    const comment = String(this.comment.value || '').trim();
    if (this.comment.invalid || (action === 'SEND_BACK' && !comment)) {
      this.comment.setErrors({ revisionRequired: true }); this.comment.markAsTouched(); this.reviewExpanded = true; return;
    }
    if (action === 'SAVE') { this.persistWorkflow(action, comment); return; }
    const ref = this.dialog.open(CellularReviewDecisionDialogComponent, { width: '480px', maxWidth: 'calc(100vw - 32px)',
      data: { status: action === 'SEND_BACK' ? 'REVIEW_REQUIRED' : action, version: this.report.version, recipient: this.review.revisionRecipient } });
    this.subscriptions.add(ref.afterClosed().subscribe(confirmed => { if (confirmed && this.canEdit) this.persistWorkflow(action, comment); }));
  }
  private persistWorkflow(action: string, comment: string): void {
    this.busy = true;
    this.subscriptions.add(this.billing.changeWorkflow(this.reportId, this.workflow.revision, action, this.report, comment, window.location.origin)
      .pipe(finalize(() => this.busy = false)).subscribe(workflow => {
        this.acceptWorkflow(workflow);
        this.toastr.success(action === 'COMPLETE' ? 'Monthly billing completed and locked.' : action === 'SUBMIT_BILLING' ? 'Submitted to final billing.' : action === 'SEND_BACK' ? 'Returned for revision.' : 'New workflow revision saved.');
        if (workflow.notification.status === 'FAILED') this.toastr.warning('Report saved, but email delivery failed. Use Retry email.');
      }, error => this.toastr.error(this.message(error, 'Unable to save. Your edits are retained; reload if another user changed the report.'))));
  }

  retryEmail(): void {
    if (!this.canRetryEmail) return;
    if (this.workflow.notificationRevision !== null) {
      if (this.busy) return;
      this.busy = true;
      this.subscriptions.add(this.billing.retryWorkflowEmail(this.reportId, this.workflow.notificationRevision, window.location.origin)
        .pipe(finalize(() => this.busy = false)).subscribe(workflow => {
          this.workflow.notification = workflow.notification;
          if (workflow.notification.status === 'SENT') this.toastr.success('Billing email sent.');
          else this.toastr.warning('Email not delivered yet. The saved billing stage is unchanged.');
        }, error => this.toastr.error(this.message(error, 'Unable to resend the email.'))));
      return;
    }
    this.busy = true;
    this.subscriptions.add(this.billing.retryRevisionEmail(this.reportId, window.location.origin)
      .pipe(finalize(() => this.busy = false)).subscribe(review => {
        this.review = review;
        if (review.notificationSent) this.toastr.success('Revision email sent.');
        else this.toastr.error('Email delivery failed. Your revision decision is still saved.');
      }, error => this.toastr.error(this.message(error, 'Unable to resend the revision email.'))));
  }

  exportReport(): void {
    if (this.dirty) { this.toastr.warning('Save your edits before exporting this revision.'); return; }
    this.exportBusy = true;
    this.subscriptions.add(this.billing.exportWorkflow(this.reportId)
      .pipe(finalize(() => this.exportBusy = false)).subscribe(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cellular-billing-${this.report.year}-${this.report.month}-v${this.report.version}-r${this.workflow.revision}.xlsx`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
      }, error => this.toastr.error(this.message(error, 'Unable to export the report.'))));
  }

  eventLabel(event): string {
    if (event.type === 'SUBMITTED') return 'Sent for verification';
    if (event.type === 'DECISION') return event.status === 'APPROVED' ? 'Report approved' : 'Returned for revision';
    return event.sent ? 'Revision email delivered' : 'Revision email delivery failed';
  }

  private message(error, fallback: string): string {
    return error && error.error && error.error.error && error.error.error.message || fallback;
  }
}
